"use client";

import React, { useState } from 'react';
import { DialogContent, DialogActions, Button, Stack, Typography, Alert, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useSelector } from 'react-redux';
import PatientSearchResults from '../../components/PatientSearchResults'; // Import PatientSearchResults

const PatientSearchForm = ({ onSearchComplete, onCancelSearch }) => {
  const theme = useTheme();
  const { chatParams, selectedChatId } = useSelector((state) => state.app);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const NewPatientSchema = Yup.object().shape({
    patientId: Yup.string(),
    firstName: Yup.string(),
    lastName: Yup.string(),
    dateOfBirth: Yup.date().nullable(),
    // cellNumber: Yup.string().matches(/^\+?[1-9]\d{1,14}$/, 'Invalid cell number format (e.g., +1234567890)').nullable(),
  }).test(
    'at-least-one-field',
    'At least one field must be filled',
    function (value) {
      return (
        value.patientId ||
        value.firstName ||
        value.lastName ||
        value.dateOfBirth ||
        value.cellNumber
      );
    }
  );

  const defaultValues = {
    patientId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    cellNumber: '',
  };

  const methods = useForm({
    resolver: yupResolver(NewPatientSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    setError,
  } = methods;

  const onSubmit = async (data) => {
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const storedUser = sessionStorage.getItem('currentUser');
      if (!storedUser) {
        setError('afterSubmit', { message: 'User not logged in. Please log in again.' });
        setIsSearching(false);
        return;
      }
      const currentUser = JSON.parse(storedUser);
      const chatToken = currentUser.chatToken;

      if (!chatToken) {
        setError('afterSubmit', { message: 'Chat token not found. Please log in again.' });
        setIsSearching(false);
        return;
      }

      const formattedDob = data.dateOfBirth ? new Date(data.dateOfBirth).toISOString().slice(0, 10).replace(/-/g, '') : '';

      const payload = {
        patientId: data.patientId,
        firstName: data.firstName,
        lastName: data.lastName,
        dob: formattedDob,
        cellNumber: data.cellNumber,
      };

      const response = await axios.post('https://backend-stgweb.meftii.com/service/getPatientRecordBySearch', payload, {
        headers: {
          Authorization: chatToken,
        },
      });

      if (response.data?.status === 'true' && Array.isArray(response.data?.data)) {
        setSearchResults(response.data.data);
        onSearchComplete(response.data.data); // Pass results up to parent
      } else {
        setSearchError(response.data?.message || 'No patients found matching your criteria.');
        onSearchComplete([]);
      }
    } catch (error) {
      console.error('Error searching for patient:', error);
      setSearchError(error.response?.data?.message || 'Failed to search for patient. Please try again.');
      onSearchComplete([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">Search Patient</Typography>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          {!!errors.afterSubmit && <Alert severity='error'>{errors.afterSubmit.message}</Alert>}
          {searchError && <Alert severity='error'>{searchError}</Alert>}

          <RHFTextField name="patientId" label="Patient ID" variant="filled" InputLabelProps={{ shrink: true }} />
          <RHFTextField name="firstName" label="First Name" variant="filled" InputLabelProps={{ shrink: true }} />
          <RHFTextField name="lastName" label="Last Name" variant="filled" InputLabelProps={{ shrink: true }} />
          <RHFTextField
            name="dateOfBirth"
            label="Date of Birth"
            type="date"
            variant="filled"
            InputLabelProps={{ shrink: true }}
          />
          <RHFTextField name="cellNumber" label="Cell Number" variant="filled" InputLabelProps={{ shrink: true }} />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onCancelSearch} disabled={isSearching}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSearching}>
              {isSearching ? <CircularProgress size={24} /> : 'Search Patient'}
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default PatientSearchForm;